import React from 'react';
import MultiSelectField from './MultiSelectField';

// Primary Site control for the protocol Add/Edit screens.
//
// The multi-select itself is MultiSelectField (the diagnosis form's five
// multi-selects all render through it); what lives here is the lead radio group
// and the id-shaped props API the protocol screens already call.
//
// The patient diagnosis screen deliberately does NOT use this — it renders
// MultiSelectField directly. A diagnosis's lead site is backend-assigned and
// ignored on input (diagnosis spec §2), so the radios would be a no-op there,
// and it needs the selection as objects rather than ids because they carry
// diagnosisPrimarySiteId and the name of any since-retired site.
//
// Deliberately NOT here:
//  - tumour-group grouping or headers. Selection is by site name only, on a
//    flat list in the order the API returned it (protocol spec §6).
//  - a "Create <x>" escape hatch. primary_site_master is a curated vocabulary;
//    it is not extensible from either screen.
//  - a hard-coded cap. Today's widest protocol has 4 sites, so the control is
//    sized for that, but the limit is not enforced (protocol spec §7).
//
// Presentational only — the parent owns the state and the fetching.
export default function PrimarySitePicker(props) {
    const {
        options,
        selectedSites,
        onChange,
        onLeadChange,
        idPrefix,
        label = 'Primary Site',
        required = false,
        error,
        loading,
        placeholder = 'Select primary site(s)',
        maxTagCount = 4,
        groupClassName,
        listHeight,
    } = props;

    const selected = Array.isArray(selectedSites) ? selectedSites : [];
    const prefix = idPrefix || 'primary-site';

    // The protocol screens' onChange takes site ids (it feeds syncSelection).
    // MultiSelectField hands back objects, so translate here rather than
    // changing a working callers' contract.
    const handleChange = (sites) => {
        if (onChange) onChange(sites.map((site) => site.siteId));
    };

    return (
        <div>
            <MultiSelectField
                label={label}
                required={required}
                error={error}
                options={options}
                value={selected}
                onChange={handleChange}
                idKey="siteId"
                labelKey="siteName"
                placeholder={placeholder}
                loading={loading}
                maxTagCount={maxTagCount}
                groupClassName={groupClassName}
                listHeight={listHeight}
                notFoundText="No matching site"
            />

            {/* Nothing is rendered below the control until at least one site is
                chosen — 7 protocols legitimately have no site, and a lone "Lead
                site:" label with no options reads as a broken required field. */}
            {selected.length > 0 && (
                <div className="mt-2">
                    <small className="text-muted d-block mb-1">Lead site</small>
                    {selected.map((site) => (
                        <div className="form-check form-check-inline" key={site.siteId}>
                            <input
                                className="form-check-input"
                                type="radio"
                                id={`${prefix}-lead-${site.siteId}`}
                                name={`${prefix}-lead`}
                                checked={site.leadSite === true}
                                onChange={() => onLeadChange(site.siteId)}
                            />
                            <label
                                className="form-check-label"
                                htmlFor={`${prefix}-lead-${site.siteId}`}
                            >
                                {site.siteName}
                            </label>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
